import { useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, Stack, TextField } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMinus, faPlus } from "@fortawesome/pro-solid-svg-icons"

const BUTTON_SIZE = "2rem"

const useStyles = makeStyles()((theme) => ({
    base: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "none",
        height: BUTTON_SIZE,
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        color: theme.palette.tailwind.slate[800],
        background: theme.palette.common.white,
    },
    button: {
        width: BUTTON_SIZE,
        cursor: "pointer",
        borderRadius: "0.625rem",
    },
    buttonDark: {
        background: theme.palette.tailwind.slate[800],
        borderColor: theme.palette.tailwind.slate[800],
        color: theme.palette.common.white,
    },
    buttonDisabled: {
        opacity: 0.4,
        cursor: "default",
    },
    buttonBlocked: {
        pointerEvents: "none",
    },
    countBox: {
        minWidth: BUTTON_SIZE,
        maxWidth: "3.5rem",
        color: theme.palette.tailwind.slate[800],
        background: theme.palette.common.white,
        "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: ".75rem",
        },
        "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.tailwind.slate[300],
        },

        "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        },
    },
    input: {
        height: BUTTON_SIZE,
        padding: "0 .25rem",
        textAlign: "center",
    },
}))

type PackageCounterProps = {
    count: number
    disableIncrease?: boolean
    blockIncrease?: boolean
    handleCounter: (el: number) => void
    onDisabledClick?: () => void
    allowZeroDisplay?: boolean
}

export const ProductCounter = ({
    count,
    handleCounter,
    disableIncrease = false,
    blockIncrease = false,
    onDisabledClick,
    allowZeroDisplay = false,
}: PackageCounterProps) => {
    const { classes, cx } = useStyles()
    const [isCountEmpty, setIsCountEmpty] = useState(false)

    const handleIncrement = () => {
        setIsCountEmpty(false)
        if (blockIncrease) return
        if (disableIncrease) {
            onDisabledClick?.()
            return
        }
        handleCounter(count + 1)
    }

    const handleDecrement = () => {
        setIsCountEmpty(false)
        handleCounter(Math.max(count - 1, 0))
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, "")
        // a cleared field is mid-edit, not a request to remove the line
        setIsCountEmpty(!value)
        if (!value) return
        const numericValue = parseInt(value, 10)

        if ((disableIncrease || blockIncrease) && numericValue > count) {
            return
        }

        handleCounter(numericValue)
    }

    const shouldShowMinusAndInput = count > 0 || allowZeroDisplay
    const shouldApplyDarkButton = count === 0 && !allowZeroDisplay

    return (
        <Stack flexDirection="row" gap="0.25rem" alignItems="center">
            {shouldShowMinusAndInput && (
                <>
                    <Box className={cx(classes.base, classes.button)} onClick={handleDecrement}>
                        <FontAwesomeIcon icon={faMinus} />
                    </Box>
                    <TextField
                        type="text"
                        value={isCountEmpty ? "" : count}
                        onChange={handleInputChange}
                        onBlur={() => setIsCountEmpty(false)}
                        size="small"
                        inputProps={{
                            className: classes.input,
                            maxLength: 4,
                        }}
                        className={classes.countBox}
                    />
                </>
            )}
            <Box
                className={cx(
                    classes.base,
                    classes.button,
                    shouldApplyDarkButton && classes.buttonDark,
                    (disableIncrease || blockIncrease) && classes.buttonDisabled,
                    blockIncrease && classes.buttonBlocked,
                )}
                onClick={handleIncrement}
                onMouseDown={(e) => e.preventDefault()}
            >
                <FontAwesomeIcon icon={faPlus} />
            </Box>
        </Stack>
    )
}
